import { defineComponent as _defineComponent } from 'vue';
import { normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, toDisplayString as _toDisplayString, unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue";
const _hoisted_1 = { class: "df-page" };
const _hoisted_2 = { class: "df-return__icon-wrap" };
const _hoisted_3 = {
    class: "df-state__card",
    role: "status",
    "aria-live": "polite",
    "aria-atomic": "true"
};
const _hoisted_4 = { class: "df-state__title" };
const _hoisted_5 = ["innerHTML"];
import { ref, computed, onMounted, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import DOMPurify from "dompurify";
import { Button } from "@eudonet/opal";
import { brandingToThemeVars } from "@/donation-form";
import { localized } from "@/donation-form/composables/localized";
import { usePaymentOutcome } from "@/composables/usePaymentOutcome";
import { getPublicForm } from "@/services/donationFormService";
import { logger } from "@/services/loggerService";
import { hostTagToDocLocale } from "@/shared/i18n";
export default /*@__PURE__*/ _defineComponent({
    __name: 'DonationReturnView',
    props: {
        slug: {}
    },
    setup(__props) {
        const props = __props;
        const CONFIRMED_OUTCOME = {
            Paid: "success",
            Failed: "failure",
            Cancelled: "cancelled",
        };
        const MSG_TYPE = {
            success: "ThankYou",
            failure: "PaymentFailed",
            cancelled: "Cancelled",
            pending: "Pending",
        };
        const { t, locale } = useI18n();
        const route = useRoute();
        const router = useRouter();
        const doc = ref(null);
        function first(raw) {
            const v = Array.isArray(raw) ? raw[0] : raw;
            return typeof v === "string" ? v : null;
        }
        // CONSTRAINT: `status=cancelled` marks the cancel URL (EmbedOriginPolicy::paymentReturnUrls), which opay also
        // takes on a refusal: until the lookup confirms an outcome, that URL reads as a failure, never as a cancel.
        const unconfirmed = first(route.query.status) === "cancelled" ? "failure" : "pending";
        const donationRef = first(route.query.ref);
        const lookup = usePaymentOutcome(props.slug, donationRef);
        const outcome = computed(() => {
            const confirmed = lookup.outcome.value;
            return confirmed === "Pending" ? unconfirmed : CONFIRMED_OUTCOME[confirmed];
        });
        const brandStyle = computed(() => doc.value ? brandingToThemeVars(doc.value.branding) : {});
        const brandClass = computed(() => (doc.value ? "df-state--branded" : "df-state--plain"));
        const activeDocLocale = computed(() => {
            if (!doc.value) {
                return null;
            }
            const fromHost = hostTagToDocLocale(locale.value);
            return doc.value.supportedLocales.includes(fromHost) ? fromHost : doc.value.defaultLocale;
        });
        const title = computed(() => t(`donationForm.return.${outcome.value}.title`));
        const body = computed(() => {
            const fallback = t(`donationForm.return.${outcome.value}.body`);
            const d = doc.value;
            if (!d || !activeDocLocale.value) {
                return fallback;
            }
            const msg = d.messages.find((m) => m.type === MSG_TYPE[outcome.value] && m.isActive);
            if (!msg) {
                return fallback;
            }
            const resolved = localized(msg.content, activeDocLocale.value, d.defaultLocale);
            return resolved || fallback;
        });
        // Author HTML: always sanitize before v-html (stored-XSS boundary).
        const bodyHtml = computed(() => DOMPurify.sanitize(body.value));
        const iconClass = computed(() => {
            switch (outcome.value) {
                case "success":
                    return "ri-check-line";
                case "failure":
                    return "ri-close-line";
                case "cancelled":
                    return "ri-information-line";
                default:
                    return "ri-time-line";
            }
        });
        const iconColor = computed(() => {
            switch (outcome.value) {
                case "success":
                    return "var(--colors-text-success)";
                case "failure":
                    return "var(--colors-text-error)";
                default:
                    return "var(--colors-text-body-subtle)";
            }
        });
        const iconStyle = computed(() => ({ color: iconColor.value }));
        const glowStyle = computed(() => ({ background: iconColor.value }));
        /** Forward only `at`: `ref`/`status` must never follow the donor back to the form. */
        function goToForm() {
            const at = first(route.query.at);
            router.push({
                name: "DonationForm",
                params: { slug: props.slug },
                ...(at !== null && at.trim() !== "" ? { query: { at } } : {}),
            });
        }
        function targetOrigin() {
            return document.referrer ? new URL(document.referrer).origin : "*";
        }
        /** May be posted to "*": never add PII (email, name, amount, address) to the payload. */
        function notifyParent(status) {
            if (typeof window === "undefined" || window.parent === window.self) {
                return;
            }
            try {
                window.parent.postMessage({ type: "donation:status", status, ref: donationRef }, targetOrigin());
            }
            catch (e) {
                logger.error("Failed to post the donation status to the embedding page", { error: String(e) });
            }
        }
        async function loadDocument() {
            try {
                const result = await getPublicForm(props.slug);
                // The donor has been through the payment step: never 404 or redirect on return.
                doc.value = result.kind === "ok" ? result.value.document : null;
            }
            catch (e) {
                doc.value = null;
                logger.error("Failed to load the donation form on return", {
                    slug: props.slug,
                    error: String(e),
                });
            }
        }
        const documentLoaded = ref(false);
        watch([documentLoaded, outcome], ([loaded, current]) => {
            if (loaded) {
                notifyParent(current);
            }
        });
        onMounted(async () => {
            await loadDocument();
            documentLoaded.value = true;
        });
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("main", _hoisted_1, [
                _createElementVNode("div", {
                    class: _normalizeClass(["df-state", brandClass.value]),
                    style: _normalizeStyle(brandStyle.value)
                }, [
                    _createElementVNode("div", _hoisted_2, [
                        _createElementVNode("span", {
                            class: "df-return__glow",
                            style: _normalizeStyle(glowStyle.value),
                            "aria-hidden": "true"
                        }, null, 4 /* STYLE */),
                        _createElementVNode("i", {
                            class: _normalizeClass([iconClass.value, "df-return__icon"]),
                            style: _normalizeStyle(iconStyle.value),
                            "aria-hidden": "true"
                        }, null, 6 /* CLASS, STYLE */)
                    ]),
                    _createElementVNode("div", _hoisted_3, [
                        _createElementVNode("h1", _hoisted_4, _toDisplayString(title.value), 1 /* TEXT */),
                        _createElementVNode("div", {
                            class: "df-state__body",
                            innerHTML: bodyHtml.value
                        }, null, 8 /* PROPS */, _hoisted_5)
                    ]),
                    _createVNode(_unref(Button), {
                        class: "df-return__cta",
                        variant: "primary",
                        "icon-left": "ri-arrow-left-line",
                        label: _ctx.$t('donationForm.return.backToForm'),
                        onClick: goToForm
                    }, null, 8 /* PROPS */, ["label"])
                ], 6 /* CLASS, STYLE */)
            ]));
        };
    }
});
